import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export function BookViewer({ pdfDoc, currentPage, onPageChange }) {
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipProgress, setFlipProgress] = useState(0);
  const [flipDirection, setFlipDirection] = useState(null);
  const [flippingPageNum, setFlippingPageNum] = useState(null);
  const [nextPageNum, setNextPageNum] = useState(null);

  const [diagnosticLog, setDiagnosticLog] = useState([]);

  const leftCanvasRef = useRef(null);
  const rightCanvasRef = useRef(null);
  const flipFrontCanvasRef = useRef(null);
  const flipBackCanvasRef = useRef(null);
  const animationRef = useRef(null);
  
  // Store viewport dimensions to prevent resizing
  const viewportDimensionsRef = useRef({ width: 0, height: 0 });

  const addLog = (message, canvasName, width, height, extra = '') => {
    const timestamp = Date.now();
    const log = `[${new Date(timestamp).toISOString().split('T')[1]}] ${message} | Canvas: ${canvasName} | Size: ${width}x${height} ${extra}`;
    console.log('🔍 DIAGNOSTIC:', log);
    setDiagnosticLog(prev => [...prev.slice(-25), log]);
  };

  // 🔥 NEW: Monitor when flip canvases come into existence
  useEffect(() => {
    if (flipFrontCanvasRef.current) {
      addLog('FLIP_FRONT canvas EXISTS in DOM', 'FLIP_FRONT', 
        flipFrontCanvasRef.current.width, 
        flipFrontCanvasRef.current.height,
        `isFlipping=${isFlipping}`);
    }
    if (flipBackCanvasRef.current) {
      addLog('FLIP_BACK canvas EXISTS in DOM', 'FLIP_BACK', 
        flipBackCanvasRef.current.width, 
        flipBackCanvasRef.current.height,
        `isFlipping=${isFlipping}`);
    }
  }, [isFlipping]);

  // Get and store viewport dimensions once
  useEffect(() => {
    const getViewportDimensions = async () => {
      if (!pdfDoc) return;
      
      const page = await pdfDoc.getPage(1);
      const viewport = page.getViewport({ scale: 1 });
      viewportDimensionsRef.current = {
        width: viewport.width,
        height: viewport.height
      };
    };
    
    getViewportDimensions();
  }, [pdfDoc]);

  useEffect(() => {
    if (pdfDoc && leftCanvasRef.current && rightCanvasRef.current) {
      renderPages();
    }
  }, [pdfDoc, currentPage]);

  const renderPages = async () => {
    if (!pdfDoc) return;

    if (currentPage <= pdfDoc.numPages) {
      await renderPageToCanvas(currentPage, leftCanvasRef.current, 'LEFT');
    }

    if (currentPage + 1 <= pdfDoc.numPages) {
      await renderPageToCanvas(currentPage + 1, rightCanvasRef.current, 'RIGHT');
    } else {
      const ctx = rightCanvasRef.current?.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, rightCanvasRef.current.width, rightCanvasRef.current.height);
      }
    }
  };

  const renderPageToCanvas = async (pageNum, canvas, canvasName) => {
    if (!pdfDoc || !canvas || pageNum > pdfDoc.numPages || pageNum < 1) return;
    
    try {
      const beforeWidth = canvas.width;
      const beforeHeight = canvas.height;
      addLog(`BEFORE render page ${pageNum}`, canvasName, beforeWidth, beforeHeight);

      const page = await pdfDoc.getPage(pageNum);
      const context = canvas.getContext('2d');

      // CRITICAL FIX: Use consistent scaling for ALL canvases
      // This prevents size changes during flip animation
      const baseViewport = page.getViewport({ scale: 1 });
      
      // Calculate a consistent scale based on container
      const containerWidth = window.innerWidth * 0.35; // Approximate half-page width
      const containerHeight = window.innerHeight * 0.6; // Approximate viewable height
      
      const scaleX = containerWidth / baseViewport.width;
      const scaleY = containerHeight / baseViewport.height;
      const scale = Math.min(scaleX, scaleY, 1.2); // Use same scale for all
      
      const viewport = page.getViewport({ scale: scale });

      // Set canvas dimensions - same for all canvases
      if (canvas.width !== viewport.width || canvas.height !== viewport.height) {
        addLog(`Setting canvas dimensions`, canvasName, canvas.width, canvas.height, 
          `→ ${viewport.width}x${viewport.height} (scale: ${scale.toFixed(2)})`);
        canvas.width = viewport.width;
        canvas.height = viewport.height;
      }

      addLog(`AFTER dimension set page ${pageNum}`, canvasName, canvas.width, canvas.height);
      
      // Clear canvas before rendering
      context.clearRect(0, 0, canvas.width, canvas.height);
      
      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;

      addLog(`COMPLETED render page ${pageNum}`, canvasName, canvas.width, canvas.height);
    } catch (err) {
      console.error('Error rendering page:', err);
    }
  };

  // Pre-initialize flip canvas with proper scaling
  const initializeFlipCanvas = async (canvas, pageNum, canvasName) => {
    if (!pdfDoc || !canvas || pageNum > pdfDoc.numPages || pageNum < 1) return;
    
    try {
      // Render with same scaling as regular pages
      await renderPageToCanvas(pageNum, canvas, canvasName);
    } catch (err) {
      console.error('Error initializing flip canvas:', err);
    }
  };

  // Render flipping page canvases when flip animation starts
  useEffect(() => {
    if (isFlipping && flippingPageNum && flipFrontCanvasRef.current) {
      console.log('🔍 FLIP START: Rendering flip front canvas');
      addLog('useEffect triggered for FLIP_FRONT', 'FLIP_FRONT', 
        flipFrontCanvasRef.current.width, 
        flipFrontCanvasRef.current.height);
      // Use the new initialization function
      initializeFlipCanvas(flipFrontCanvasRef.current, flippingPageNum, 'FLIP_FRONT');
    }
    if (isFlipping && nextPageNum && flipBackCanvasRef.current) {
      console.log('🔍 FLIP START: Rendering flip back canvas');
      addLog('useEffect triggered for FLIP_BACK', 'FLIP_BACK', 
        flipBackCanvasRef.current.width, 
        flipBackCanvasRef.current.height);
      // Use the new initialization function
      initializeFlipCanvas(flipBackCanvasRef.current, nextPageNum, 'FLIP_BACK');
    }
  }, [isFlipping, flippingPageNum, nextPageNum]);

  const animateFlip = (direction, targetPage) => {
    let start = null;
    const duration = 600;

    const animate = (timestamp) => {
      if (!start) start = timestamp;
      const elapsed = timestamp - start;
      const progress = Math.min(elapsed / duration, 1);

      const eased = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      setFlipProgress(eased);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsFlipping(false);
        setFlipDirection(null);
        setFlipProgress(0);
        onPageChange(targetPage);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  const handleNextPage = () => {
    if (!pdfDoc || currentPage + 1 >= pdfDoc.numPages || isFlipping) return;

    console.log('🔍 NEXT PAGE CLICKED - Watch for canvas dimension changes!');
    addLog('>>> BUTTON CLICKED: handleNextPage', 'SYSTEM', 0, 0);

    setFlippingPageNum(currentPage + 1);
    setNextPageNum(currentPage + 2);

    setFlipDirection('forward');
    
    addLog('>>> ABOUT TO SET isFlipping=true', 'SYSTEM', 0, 0);
    setIsFlipping(true);
    addLog('>>> AFTER SET isFlipping=true', 'SYSTEM', 0, 0);
    
    animateFlip('forward', currentPage + 2);
  };

  const handlePrevPage = () => {
    if (currentPage <= 1 || isFlipping) return;

    console.log('🔍 PREV PAGE CLICKED - Watch for canvas dimension changes!');
    addLog('>>> BUTTON CLICKED: handlePrevPage', 'SYSTEM', 0, 0);

    setFlippingPageNum(currentPage);
    setNextPageNum(currentPage - 1);

    setFlipDirection('backward');
    
    addLog('>>> ABOUT TO SET isFlipping=true', 'SYSTEM', 0, 0);
    setIsFlipping(true);
    addLog('>>> AFTER SET isFlipping=true', 'SYSTEM', 0, 0);
    
    animateFlip('backward', Math.max(1, currentPage - 2));
  };

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const getFlipTransform = () => {
    if (!isFlipping) return {};

    const angle = flipProgress * 180;

    if (flipDirection === 'forward') {
      return {
        transform: `rotateY(-${angle}deg)`,
        transformOrigin: 'left center',
      };
    } else {
      return {
        transform: `rotateY(${angle}deg)`,
        transformOrigin: 'right center',
      };
    }
  };

  const getPageShadow = () => {
    if (!isFlipping) return 'none';

    const intensity = Math.sin(flipProgress * Math.PI) * 0.5;

    if (flipDirection === 'forward') {
      return `inset -${intensity * 40}px 0 ${intensity * 60}px rgba(0,0,0,${intensity * 0.6})`;
    } else {
      return `inset ${intensity * 40}px 0 ${intensity * 60}px rgba(0,0,0,${intensity * 0.6})`;
    }
  };

  return (
    <div style={{
      width: '100%',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '2rem 2rem 0rem 2rem',
      overflow: 'hidden',
      perspective: '2000px',
    }}>
      {/* Diagnostic Panel */}
      {/* <div style={{
        position: 'fixed',
        top: '10px',
        left: '10px',
        width: '600px',
        maxHeight: '200px',
        overflowY: 'auto',
        background: 'rgba(0,0,0,0.85)',
        color: '#00ff00',
        fontFamily: 'monospace',
        fontSize: '10px',
        padding: '10px',
        borderRadius: '5px',
        zIndex: 1000,
        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
      }}>
        <div style={{ marginBottom: '5px', color: '#ffff00', fontWeight: 'bold' }}>
          🔍 Canvas Dimension Diagnostics
        </div>
        {diagnosticLog.map((log, idx) => (
          <div key={idx} style={{ 
            marginBottom: '2px',
            color: log.includes('RESIZING') ? '#ff6666' : 
                   log.includes('FLIP_') ? '#66bbff' :
                   log.includes('>>>') ? '#ffaa00' :
                   log.includes('EXISTS') ? '#ff66ff' : '#00ff00'
          }}>
            {log}
          </div>
        ))}
      </div> */}

      <div style={{
        display: 'flex',
        width: '90%',
        maxWidth: '1400px',
        height: '80%',
        background: 'white',
        borderRadius: '15px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 0 120px rgba(103,126,234,0.4)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '3rem',
          background: 'linear-gradient(to right, #ffffff 0%, #fefdfb 100%)',
          position: 'relative',
          boxShadow: 'inset -8px 0 20px rgba(0,0,0,0.08)',
          borderRight: '1px solid rgba(0,0,0,0.05)',
        }}>
          <div style={{
            maxWidth: '100%',
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2rem',
          }}>
            <canvas
              ref={leftCanvasRef}
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(100% - 3rem)',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                borderRadius: '2px',
              }}
            />
            <div style={{
              fontSize: '0.875rem',
              color: '#666',
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
            }}>
              {currentPage}
            </div>
          </div>
        </div>

        {/* Flipping Page Overlay */}
        {isFlipping && (
          <div style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: flipDirection === 'backward' ? 0 : '50%',
            right: flipDirection === 'forward' ? 0 : '50%',
            transformStyle: 'preserve-3d',
            ...getFlipTransform(),
            transition: 'none',
            zIndex: 10,
            pointerEvents: 'none',
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(to right, #fefdfb 0%, #ffffff 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '3rem',
              backfaceVisibility: 'hidden',
              boxShadow: getPageShadow(),
              position: 'absolute',
              top: 0,
              left: 0,
            }}>
              <canvas
                ref={flipFrontCanvasRef}
                style={{
                  maxWidth: '100%',
                  maxHeight: 'calc(100% - 3rem)',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                  borderRadius: '2px',
                }}
              />
            </div>

            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to left, #fefdfb 0%, #f8f6f0 100%)',
              transform: 'rotateY(180deg)',
              backfaceVisibility: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '3rem',
            }}>
              <canvas
                ref={flipBackCanvasRef}
                style={{
                  maxWidth: '100%',
                  maxHeight: 'calc(100% - 3rem)',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                  borderRadius: '2px',
                }}
              />
            </div>
          </div>
        )}

        <div style={{
          position: 'absolute',
          left: '50%',
          top: '5%',
          bottom: '5%',
          width: '3px',
          background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.15) 10%, rgba(0,0,0,0.15) 90%, transparent 100%)',
          transform: 'translateX(-50%)',
          zIndex: 5,
          pointerEvents: 'none',
          boxShadow: '0 0 15px rgba(0,0,0,0.2)',
        }} />

        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '3rem',
          background: 'linear-gradient(to left, #fefdfb 0%, #ffffff 100%)',
          position: 'relative',
          boxShadow: 'inset 8px 0 20px rgba(0,0,0,0.08)',
          borderLeft: '1px solid rgba(0,0,0,0.05)',
        }}>
          <div style={{
            maxWidth: '100%',
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2rem',
          }}>
            <canvas
              ref={rightCanvasRef}
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(100% - 3rem)',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                borderRadius: '2px',
              }}
            />

            {currentPage + 1 <= pdfDoc?.numPages && (
              <div style={{
                fontSize: '0.875rem',
                color: '#666',
                fontFamily: 'Georgia, serif',
                fontStyle: 'italic',
              }}>
                {currentPage + 1}
              </div>
            )}
          </div>
        </div>

        <button
          onClick={handlePrevPage}
          disabled={currentPage <= 1 || isFlipping}
          style={{
            position: 'absolute',
            left: '-1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '3.5rem',
            height: '3.5rem',
            borderRadius: '50%',
            border: '3px solid rgba(255,255,255,0.2)',
            background: currentPage <= 1 ? 'linear-gradient(135deg, #94a3b8, #64748b)' : 'linear-gradient(135deg, #a78bfa, #8b5cf6)',
            color: 'white',
            cursor: currentPage <= 1 || isFlipping ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 25px rgba(139, 92, 246, 0.4)',
            opacity: currentPage <= 1 ? 0.4 : 1,
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 20,
            backdropFilter: 'blur(10px)',
          }}
          onMouseEnter={(e) => {
            if (currentPage > 1 && !isFlipping) {
              e.currentTarget.style.transform = 'translateY(-50%) scale(1.15) rotate(-5deg)';
              e.currentTarget.style.boxShadow = '0 12px 35px rgba(139, 92, 246, 0.6)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(139, 92, 246, 0.4)';
          }}
        >
          <ChevronLeft size={28} strokeWidth={2.5} />
        </button>

        <button
          onClick={handleNextPage}
          disabled={!pdfDoc || currentPage + 1 >= pdfDoc?.numPages || isFlipping}
          style={{
            position: 'absolute',
            right: '-1rem',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '3.5rem',
            height: '3.5rem',
            borderRadius: '50%',
            border: '3px solid rgba(255,255,255,0.2)',
            background: (!pdfDoc || currentPage + 1 >= pdfDoc?.numPages) ? 'linear-gradient(135deg, #94a3b8, #64748b)' : 'linear-gradient(135deg, #a78bfa, #8b5cf6)',
            color: 'white',
            cursor: (!pdfDoc || currentPage + 1 >= pdfDoc?.numPages || isFlipping) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 25px rgba(139, 92, 246, 0.4)',
            opacity: (!pdfDoc || currentPage + 1 >= pdfDoc?.numPages) ? 0.4 : 1,
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 20,
            backdropFilter: 'blur(10px)',
          }}
          onMouseEnter={(e) => {
            if (pdfDoc && currentPage + 1 < pdfDoc.numPages && !isFlipping) {
              e.currentTarget.style.transform = 'translateY(-50%) scale(1.15) rotate(5deg)';
              e.currentTarget.style.boxShadow = '0 12px 35px rgba(139, 92, 246, 0.6)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(139, 92, 246, 0.4)';
          }}
        >
          <ChevronRight size={28} strokeWidth={2.5} />
        </button>
      </div>

      {/* <div style={{
        marginTop: '2rem',
        padding: '1rem 2rem',
        textAlign: 'center',
        background: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(20px)',
        borderRadius: '2rem',
        border: '1px solid rgba(255,255,255,0.2)',
        fontSize: '0.875rem',
        color: '#e0e7ff',
        fontFamily: 'Georgia, serif',
        boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
        maxWidth: 'fit-content',
        margin: '2rem auto 0',
      }}>
        <span style={{ fontWeight: 'bold', color: '#c7d2fe' }}>Pages {currentPage}-{Math.min(currentPage + 1, pdfDoc?.numPages || 0)}</span>
        <span style={{ margin: '0 0.75rem', color: '#818cf8' }}>•</span>
        <span style={{ color: '#a5b4fc' }}>Total: {pdfDoc?.numPages || 0}</span>
      </div> */}
    </div>
  );
}